import { createServerFn } from "@tanstack/react-start";

import { authMiddleware, mondayBoards } from "@onesolution/middlewares";

import queryClient from "@onesolution/components/ProvidersWrapper/queryClient";
import { NOTIFICATIONS_COLUMNS } from "@onesolution/constants/monday/notifications";
import type { MONDAY_TYPES } from "@onesolution/monday";
import { mondayClient } from "@onesolution/monday/monday";
import z from "zod";
import { wrapWithUseMutation } from "..";

const markNotifAsReadParams = z.object({
	id: z.string().optional(),
	pulseId: z.string().optional(),
	delete: z.boolean().optional(),
});

const handler = createServerFn({ method: "POST" })
	.middleware([authMiddleware, mondayBoards])
	.validator(markNotifAsReadParams)
	.handler(async ({ context, data }) => {
		if (!data.id && !data.pulseId)
			throw new Error("invalid params: need one of id or pulseId");

		const userRaw = await mondayClient.query.userInfoByEmail(
			context,
			context.user.email,
		);

		const concernedNotifIds = data.id
			? [data.id]
			: (
					await mondayClient.query.itemPageByValues({
						boardId: context.boards?.notifications,
						columnIds: [NOTIFICATIONS_COLUMNS.pulseId],
						filter: [
							{
								column_id: NOTIFICATIONS_COLUMNS.pulseId,
								column_values: [data.pulseId!],
							},
						],
					})
				).items.map((i) => i.id);

		const notifs = await mondayClient.query.items<{
			processed: MONDAY_TYPES["text"];
			userIds: MONDAY_TYPES["text"];
		}>({
			columnIds: [
				NOTIFICATIONS_COLUMNS.processed,
				NOTIFICATIONS_COLUMNS.userIds,
			],
			itemIds: concernedNotifIds,
		});

		for (const notif of notifs) {
			const processed = JSON.parse(notif.processed || "[]") as string[];
			const userIds = JSON.parse(notif.userIds || "[]") as string[];
			const newUserIds = userIds.filter((u) => u !== userRaw.id);

			await mondayClient.mutation.updateItem({
				boardId: context.boards.notifications,
				itemId: notif.id,
				columnValues: {
					[NOTIFICATIONS_COLUMNS.processed]: JSON.stringify(
						processed.concat(
							processed.includes(userRaw.id) ? [] : [userRaw.id],
						),
					),
					...(data.delete
						? { [NOTIFICATIONS_COLUMNS.userIds]: JSON.stringify(newUserIds) }
						: {}),
				},
			});
		}

		return true;
	});

export const markNotifAsReadOnServer = wrapWithUseMutation(handler, {
	onSuccess: () => {
		queryClient.invalidateQueries({
			queryKey: ["currentUserNotifCount", "currentUserNotif"],
		});
	},
});
